import type { CreateRoleInput, Role, UpdateRoleInput } from '@/application/permissions/permissionsTypes';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3010/api';

interface ErrorResponse {
  error?: string;
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ErrorResponse;
    return body.error ?? 'permissions_request_failed';
  } catch {
    return 'permissions_request_failed';
  }
}

async function requestJson<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  // DELETE responde 204 sin cuerpo.
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export function listRoles(token: string): Promise<Role[]> {
  return requestJson<Role[]>(token, '/permissions/roles');
}

export function createRole(token: string, input: CreateRoleInput): Promise<Role> {
  return requestJson<Role>(token, '/permissions/roles', { method: 'POST', body: JSON.stringify(input) });
}

export function updateRole(token: string, id: string, input: UpdateRoleInput): Promise<Role> {
  return requestJson<Role>(token, `/permissions/roles/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export function duplicateRole(token: string, id: string, name?: string): Promise<Role> {
  return requestJson<Role>(token, `/permissions/roles/${encodeURIComponent(id)}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export function deleteRole(token: string, id: string): Promise<void> {
  return requestJson<void>(token, `/permissions/roles/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
